import IntegrationCard from '@/components/integrations/IntegrationCard';
import type { IntegrationComponentProps } from '@/components/integrations/integration-catalog';
import { useMcpConnectionsQuery } from '@/hooks/queries/use-mcp-query';
import { usePlanGates } from '@/hooks/use-plan-gates';
import { PlanLimit } from '@fluenceur/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Button, Skeleton, Stack, Text } from '@chakra-ui/react';
import { ConnectedAssistantsManagement } from '@/pages/settings/account/ConnectedAssistantsManagement';
import { AssistantConnectDialog } from './AssistantConnectDialog';
import {
  ASSISTANT_PROVIDERS,
  assistantConnectionSummary,
  type ConnectableAssistant,
} from './assistant-providers';

interface AssistantIntegrationProps extends IntegrationComponentProps {
  provider: ConnectableAssistant;
}

/**
 * An assistant is not a row of `/dashboard/integrations`: its state is the brand's
 * live MCP connections, so the connection props the page hands every integration
 * are ignored here. Its existing integration detail reuses account-owned
 * permission reduction, activity and per-connection revocation controls.
 */
const AssistantIntegration: React.FC<AssistantIntegrationProps> = ({
  provider,
  variant = 'row',
}) => {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const query = useMcpConnectionsQuery();
  const { data } = query;
  const { mcpLocked, openUpsell } = usePlanGates();
  const { name, logo } = ASSISTANT_PROVIDERS[provider];
  const { isConnected, brandNames } = assistantConnectionSummary(data?.connections, provider);

  const detailHref = `/settings/integrations/${provider}`;
  if (query.isLoading || (!data && !query.isError)) {
    return <Skeleton h={variant === 'detail' ? '200px' : '80px'} aria-label={t('mcp.loading')} />;
  }
  if (query.isError && !data) {
    return (
      <Stack gap={3} p={4}>
        <Text fontWeight="semibold">{name}</Text>
        <Alert status="error" title={t('mcp.settings.loadError')} />
        <Button variant="outline" size="sm" alignSelf="start" onClick={() => void query.refetch()}>
          {t('mcp.retry')}
        </Button>
      </Stack>
    );
  }

  return (
    <>
      <IntegrationCard
        name={name}
        description={t(`integrations.assistants.${provider}.description`)}
        icon={logo}
        iconStyle="mark"
        isActive={isConnected}
        // A plan without connected assistants presents them instead of
        // starting a connection the API refuses; a connection already made
        // stays reachable, to narrow or revoke it.
        onToggle={() =>
          mcpLocked && !isConnected ? openUpsell(PlanLimit.MCP) : setIsDialogOpen(true)
        }
        showConfirmDialog={false}
        showDisconnectButton={false}
        disabled={query.isError}
        inlineAction={variant === 'row' ? (
          <Button asChild variant="outline" size="sm" colorPalette="gray">
            <RouterLink to={detailHref}>{t('integrations.card.manage')}</RouterLink>
          </Button>
        ) : undefined}
        connectedLabel={t('integrations.assistants.connected')}
        connectedAs={brandNames.join(', ')}
        variant={variant}
        detailHref={detailHref}
      >
        {variant === 'detail' && <ConnectedAssistantsManagement provider={provider} />}
      </IntegrationCard>
      <AssistantConnectDialog
        provider={provider}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />
    </>
  );
};

export const ClaudeIntegration: React.FC<IntegrationComponentProps> = (props) => (
  <AssistantIntegration {...props} provider="claude" />
);

export const ChatGptIntegration: React.FC<IntegrationComponentProps> = (props) => (
  <AssistantIntegration {...props} provider="chatgpt" />
);
